/**
 * The thirteen administrative regions and their governorates.
 *
 * ⚠️ **A mirror of the server's `SaudiRegions`, and it must stay one.** The region is not a label:
 * a stranded callout goes to volunteers whose stored region MATCHES the mission's, character for
 * character (§23.4). A panel that let an operator type «الجوف » with a trailing space, or «Al-Jouf»,
 * would open a registration nobody in that region is ever told about — and nothing would report an
 * error, because both strings are valid text.
 *
 * ⚠️ Mirrored rather than fetched. There is no endpoint that lists them, and adding one to serve a
 * dropdown of thirteen fixed names would be a request on every form load for a list that changes
 * when the Kingdom redraws its provinces. The guard against drift is the test beside this file.
 */
export const SAUDI_REGIONS: Readonly<Record<string, readonly string[]>> = {
  الرياض: [
    'الرياض',
    'الخرج',
    'الدوادمي',
    'المجمعة',
    'القويعية',
    'وادي الدواسر',
    'الأفلاج',
    'الزلفي',
    'شقراء',
    'حوطة بني تميم',
    'عفيف',
    'السليل',
    'ضرما',
    'المزاحمية',
    'رماح',
    'ثادق',
    'حريملاء',
    'الحريق',
    'الغاط',
    'مرات',
    'الدلم',
  ],
  'مكة المكرمة': [
    'مكة المكرمة',
    'جدة',
    'الطائف',
    'القنفذة',
    'الليث',
    'رابغ',
    'خليص',
    'الخرمة',
    'رنية',
    'تربة',
    'الجموم',
    'الكامل',
    'بحرة',
    'العرضيات',
    'أضم',
    'ميسان',
  ],
  'المدينة المنورة': [
    'المدينة المنورة',
    'ينبع',
    'العلا',
    'المهد',
    'الحناكية',
    'بدر',
    'خيبر',
    'العيص',
    'وادي الفرع',
  ],
  القصيم: [
    'بريدة',
    'عنيزة',
    'الرس',
    'المذنب',
    'البكيرية',
    'البدائع',
    'الأسياح',
    'النبهانية',
    'عيون الجواء',
    'رياض الخبراء',
    'الشماسية',
    'ضرية',
    'عقلة الصقور',
  ],
  الشرقية: [
    'الدمام',
    'الأحساء',
    'حفر الباطن',
    'الجبيل',
    'القطيف',
    'الخبر',
    'الخفجي',
    'رأس تنورة',
    'بقيق',
    'النعيرية',
    'قرية العليا',
    'العديد',
  ],
  عسير: [
    'أبها',
    'خميس مشيط',
    'بيشة',
    'النماص',
    'محايل عسير',
    'ظهران الجنوب',
    'تثليث',
    'سراة عبيدة',
    'رجال ألمع',
    'أحد رفيدة',
    'بلقرن',
    'المجاردة',
    'طريب',
    'تنومة',
    'البرك',
    'بارق',
  ],
  تبوك: ['تبوك', 'الوجه', 'ضباء', 'تيماء', 'أملج', 'حقل', 'البدع'],
  حائل: ['حائل', 'بقعاء', 'الغزالة', 'الشنان', 'الحائط', 'السليمي', 'موقق', 'الشملي'],
  'الحدود الشمالية': ['عرعر', 'رفحاء', 'طريف', 'العويقيلة'],
  جازان: [
    'جازان',
    'صبيا',
    'أبو عريش',
    'صامطة',
    'الحرث',
    'ضمد',
    'الريث',
    'بيش',
    'فرسان',
    'الدائر',
    'العارضة',
    'أحد المسارحة',
    'العيدابي',
    'الطوال',
    'هروب',
    'فيفاء',
  ],
  نجران: ['نجران', 'شرورة', 'حبونا', 'بدر الجنوب', 'يدمة', 'ثار', 'خباش'],
  الباحة: [
    'الباحة',
    'بلجرشي',
    'المندق',
    'المخواة',
    'قلوة',
    'العقيق',
    'القرى',
    'غامد الزناد',
    'الحجرة',
    'بني حسن',
  ],
  الجوف: ['سكاكا', 'القريات', 'دومة الجندل', 'طبرجل'],
};

/** The region names, in the order the forms list them. */
export const REGION_NAMES: readonly string[] = Object.keys(SAUDI_REGIONS);

/** The governorates of a region — empty for a name that is not one. */
export function citiesOf(region: string): readonly string[] {
  return SAUDI_REGIONS[region] ?? [];
}
